/**
 * Furthest-distance ("range outline") records arrive in two shapes: the
 * WebSocket `furthest_distance_update` push carries `distance`, the HTTP
 * `getMaxDistancePoints` snapshot carries `distanceKm`. Read them through
 * these helpers so no consumer silently gets 0 from the other shape.
 */

/** Distance in km of one bearing record, 0 when absent. */
export function recordDistanceKm(record) {
  if (!record) return 0;
  const d = record.distance ?? record.distanceKm;
  return typeof d === 'number' && Number.isFinite(d) ? d : 0;
}

/**
 * Furthest record across `{ [receiverUuid]: { bearingRecords } }`, optionally
 * for one receiver only.
 * @param {Object} dataByReceiver
 * @param {string|null} [receiverUuid] restrict to this receiver; null = all
 * @returns {number} km, 0 when there is nothing
 */
export function maxRangeKm(dataByReceiver, receiverUuid = null) {
  let max = 0;
  Object.entries(dataByReceiver || {}).forEach(([uuid, rec]) => {
    if (receiverUuid && uuid !== receiverUuid) return;
    (rec?.bearingRecords || []).forEach(b => {
      const d = recordDistanceKm(b);
      if (d > max) max = d;
    });
  });
  return max;
}

/**
 * Per-receiver merge of the HTTP snapshot and WebSocket pushes. A push carries
 * that receiver's complete outline, so it replaces the snapshot for that
 * receiver only; receivers the socket has not pushed for keep their snapshot.
 * Returns null when there is nothing at all (callers treat null as "no data").
 */
export function mergeRangeData(httpSnapshot, wsPushes) {
  const http = httpSnapshot || {};
  const ws = wsPushes || {};
  if (Object.keys(http).length === 0 && Object.keys(ws).length === 0) return null;
  return { ...http, ...ws };
}

/**
 * The track a range record belongs to: the one whose [startTime, endTime]
 * interval is nearest the record's timestamp (zero when it falls inside).
 * Nearest-interval rather than strict containment so a timezone skew between
 * the record and track timestamps still lands on the right flight. Falls back
 * to the newest track when the record has no usable timestamp. Order of
 * `tracks` does not matter.
 */
export function pickTrackForRecord(tracks, recordTimestamp) {
  if (!tracks || tracks.length === 0) return null;
  const startOf = t => new Date(t.startTime).getTime();
  const recordMs = recordTimestamp != null ? new Date(recordTimestamp).getTime() : NaN;
  if (Number.isNaN(recordMs)) {
    return tracks.reduce((a, b) => (startOf(b) > startOf(a) ? b : a));
  }
  const gapMs = t => {
    const start = startOf(t);
    const end = t.endTime ? new Date(t.endTime).getTime() : start;
    if (recordMs < start) return start - recordMs;
    if (recordMs > end) return recordMs - end;
    return 0;
  };
  return tracks.reduce((a, b) => (gapMs(b) < gapMs(a) ? b : a));
}
